import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { phoneValidation, applyPhoneMask } from '../../utils/phoneValidation';
import { applyCpfMask, validateCPF } from '../../utils/fieldMasks';
import { CHECKOUT_PUBLIC_ERROR_MESSAGE } from '../../utils/checkoutPublicError';
import { formatUnknownError } from '../../utils/formatUnknownError';
import { calculateTicketTotalsWithFee, roundMoney } from '../../utils/money';
import { customFieldAppliesToTicket, normalizeCustomFieldScope } from '../../utils/customFieldTicketScope';
import {
  addTicketToCart,
  clearTicketCart,
  expandCartToSlots,
  getRegistrationQuantityLimits,
  loadTicketCart,
  type TicketCartSlot,
} from '../../utils/ticketCart';
import { registrationService } from '../../services/registrationService';
import paymentServiceMercadoPago from '../../services/mercadoPagoService';
import LegalModal from '../../components/LegalModal';
import { legalService, purchaseAcceptedFromTemplates, type LegalAcceptedItem } from '../../services/legalService';
import CheckoutPaymentRouter, { type CheckoutTab } from './components/CheckoutPaymentRouter';
import PixCopyCodeField from './components/PixCopyCodeField';
import RegistrationCustomFieldInputs, {
  type CheckoutCustomField,
} from './components/RegistrationCustomFieldInputs';
import type { PaymentProviderRoutesDto } from '../../types/checkoutPayment';
import RegistrationSuccessView, {
  type RegistrationSuccessAttendee,
} from './components/RegistrationSuccessView';
import { readMetaBrowserContext, useEventMetaPixel } from '../../features/meta-pixel';

type TicketData = {
  _id: string;
  name: string;
  price: number;
  quantity: number;
  ticketTypeId?: string;
  showPlatformFee?: boolean;
  isRestricted?: boolean;
  allowedEmails?: string[];
  allowInstallments?: boolean;
  endSaleDate?: string;
  minPerOrder?: number;
  maxPerOrder?: number | null;
  /** Default true — mesmo e-mail entre participantes deste ingresso. */
  allowSameAttendeeData?: boolean;
};

type CustomFieldData = CheckoutCustomField & {
  appliesToAllTickets?: boolean;
  ticketScopeIds?: string[];
};

type EventData = {
  _id: string;
  title: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  address?: string;
  city?: string;
  state?: string;
  coverImage?: string;
  tickets: TicketData[];
  customFields?: CustomFieldData[];
  feePercentage?: number;
  allowInstallments?: boolean;
  allowGroupRegistration?: boolean;
  postRegistrationUrl?: string;
  metaPixel?: {
    pixelId?: string;
    enabled?: boolean;
  };
  requiresBuyerCpf?: boolean;
  status?: string;
};

type SuccessSummary = {
  attendees: RegistrationSuccessAttendee[];
  totalAmount: number;
  needsPayment: boolean;
};

type AttendeeForm = {
  ticketId: string;
  ticketName: string;
  name: string;
  email: string;
  emailConfirmation: string;
  phone: string;
  customFields: Record<string, string | boolean | string[]>;
};

type CartCheckoutForm = {
  attendees: AttendeeForm[];
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  buyerCpf: string;
  useFirstAsBuyer: boolean;
  agreeTerms: boolean;
};

type Props = {
  event: EventData;
  eventSlug: string;
  preSelectedTicketId?: string | null;
  refCode?: string;
};

const attendeeSchema = yup.object({
  ticketId: yup.string().required(),
  ticketName: yup.string(),
  name: yup.string().required('Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  emailConfirmation: yup
    .string()
    .email('Email inválido')
    .required('Confirme o email')
    .oneOf([yup.ref('email')], 'Os emails não coincidem'),
  phone: yup.string().required('Telefone é obrigatório').test('phone', 'Telefone inválido', phoneValidation()),
  customFields: yup.object(),
});

const schema = yup.object({
  attendees: yup.array().of(attendeeSchema).min(1),
  buyerName: yup.string(),
  buyerEmail: yup.string().email('Email inválido'),
  buyerPhone: yup.string(),
  buyerCpf: yup.string(),
  useFirstAsBuyer: yup.boolean(),
  agreeTerms: yup.boolean().oneOf([true], 'Você deve concordar com os termos'),
});

const emptyAttendee = (slot: TicketCartSlot): AttendeeForm => ({
  ticketId: slot.ticketId,
  ticketName: slot.name,
  name: '',
  email: '',
  emailConfirmation: '',
  phone: '',
  customFields: {},
});

function formatEventWhen(dateString?: string): string {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), "EEE, dd MMM yyyy · HH:mm", { locale: ptBR });
  } catch {
    return dateString;
  }
}

function formatEventWhere(event: EventData): string {
  const parts = [event.location, event.city, event.state].filter(
    (part) => typeof part === 'string' && part.trim()
  );
  return parts.join(' · ');
}

const EventCartCheckout: React.FC<Props> = ({ event, eventSlug, preSelectedTicketId, refCode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const steps = ['Participantes', 'Pagamento'];
  const [activeStep, setActiveStep] = useState(0);
  const [expandedPerson, setExpandedPerson] = useState<number | false>(0);
  const [slots, setSlots] = useState<TicketCartSlot[]>([]);
  const [linkTicketQty, setLinkTicketQty] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [registrationIds, setRegistrationIds] = useState<string[]>([]);
  const [orderAmount, setOrderAmount] = useState(0);
  const [success, setSuccess] = useState(false);
  const [successSummary, setSuccessSummary] = useState<SuccessSummary | null>(null);
  const [pixQRCodeBase64, setPixQRCodeBase64] = useState<string | null>(null);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [pixPaymentPending, setPixPaymentPending] = useState(false);
  const [cardPaymentPending, setCardPaymentPending] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy' | 'refund'>('terms');
  const [legalAccepted, setLegalAccepted] = useState<LegalAcceptedItem[]>([]);
  const [checkoutPaymentTab, setCheckoutPaymentTab] = useState<CheckoutTab>('pix');
  const [paymentProviderRoutes, setPaymentProviderRoutes] = useState<PaymentProviderRoutesDto>({
    pix: 'mercadopago',
    credit_card: 'mercadopago',
    debit_card: 'mercadopago',
  });
  const [paymentRoutesReady, setPaymentRoutesReady] = useState(false);
  const [platformInstallmentSettings, setPlatformInstallmentSettings] = useState({
    enabled: true,
    maxInstallments: 12,
  });
  const purchaseTrackedRef = useRef(false);
  const purchaseEventIdRef = useRef<string | null>(null);
  const cartAddTrackedRef = useRef(false);
  const paymentInfoTrackedRef = useRef(false);

  const {
    trackAddToCart: trackMetaAddToCart,
    trackAddPaymentInfo: trackMetaAddPaymentInfo,
    trackPurchase: trackMetaPurchase,
  } = useEventMetaPixel({
    eventSlug,
    eventId: event._id,
    eventTitle: event.title,
    metaPixel: event.metaPixel,
    // PageView e InitiateCheckout já disparam no EventRegistrationPage pai.
    trackCheckoutOnMount: false,
    disabled: event.status === 'test',
  });

  useEffect(() => {
    legalService
      .listTemplates()
      .then((templates) => setLegalAccepted(purchaseAcceptedFromTemplates(templates)))
      .catch(() => undefined);
  }, []);

  const methods = useForm<CartCheckoutForm>({
    resolver: yupResolver(schema) as Resolver<CartCheckoutForm>,
    defaultValues: {
      attendees: [],
      buyerName: '',
      buyerEmail: '',
      buyerPhone: '',
      buyerCpf: '',
      useFirstAsBuyer: true,
      agreeTerms: false,
    },
    mode: 'onChange',
  });
  const { control, handleSubmit, watch, getValues, setValue, trigger, formState: { errors } } = methods;
  const attendees = watch('attendees');
  const useFirstAsBuyer = watch('useFirstAsBuyer');

  const orderCapacityLabel = useMemo(() => {
    const people = attendees || [];
    if (people.length === 0) return null;
    return people.length === 1 ? '1 pessoa no pedido' : `${people.length} pessoas no pedido`;
  }, [attendees]);

  useEffect(() => {
    const people = attendees || [];
    if (people.length === 0) {
      setExpandedPerson(false);
      return;
    }
    if (expandedPerson === false || expandedPerson >= people.length) {
      setExpandedPerson(people.length - 1);
    }
  }, [attendees, expandedPerson]);

  const ticketBreakdown = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; qty: number; unitLabel: string; lineTotal: number }
    >();
    slots.forEach((slot) => {
      const ticket = event.tickets.find((item) => String(item._id) === String(slot.ticketId));
      const key = String(slot.ticketId);
      const unitPrice = ticket?.price ?? slot.price;
      const existing = groups.get(key);
      if (existing) {
        existing.qty += 1;
        existing.lineTotal = roundMoney(existing.lineTotal + unitPrice);
      } else {
        groups.set(key, {
          name: ticket?.name || slot.name,
          qty: 1,
          unitLabel: unitPrice > 0 ? `R$ ${unitPrice.toFixed(2).replace('.', ',')}` : 'Gratuito',
          lineTotal: roundMoney(unitPrice),
        });
      }
    });
    return Array.from(groups.values());
  }, [event.tickets, slots]);

  /** Preço exibido no card da pessoa = preço do ingresso (sem taxa). Taxa só no total. */
  const formatParticipantTicketPrice = useCallback(
    (ticketId: string) => {
      const ticket = event.tickets.find((item) => String(item._id) === String(ticketId));
      const unitPrice = Number(ticket?.price) || 0;
      if (unitPrice <= 0) return 'Gratuito';
      return `R$ ${unitPrice.toFixed(2).replace('.', ',')}`;
    },
    [event.tickets]
  );

  /** Se algum ingresso do pedido exige e-mails distintos. */
  const requireDistinctEmails = useMemo(
    () =>
      (attendees || []).some((person) => {
        const ticket = event.tickets.find((item) => String(item._id) === String(person.ticketId));
        return ticket?.allowSameAttendeeData === false;
      }),
    [attendees, event.tickets]
  );

  const catalogTickets = useMemo(
    () =>
      (event.tickets || []).map((ticket) => ({
        _id: String(ticket._id),
        name: ticket.name,
        ticketTypeId: ticket.ticketTypeId,
      })),
    [event.tickets]
  );

  const fieldsForTicket = useCallback(
    (ticketId: string) => {
      const ticket = event.tickets.find((item) => String(item._id) === String(ticketId));
      return (event.customFields || [])
        .map((field) => normalizeCustomFieldScope(field) as CustomFieldData)
        .filter((field) => customFieldAppliesToTicket(field, String(ticketId), ticket, catalogTickets));
    },
    [catalogTickets, event.customFields, event.tickets]
  );

  const calculateTotals = useCallback(() => {
    return slots.reduce(
      (acc, slot) => {
        const ticket = event.tickets.find((item) => String(item._id) === String(slot.ticketId));
        const line = calculateTicketTotalsWithFee({
          unitPrice: ticket?.price ?? slot.price,
          quantity: 1,
          feePercentage: event.feePercentage,
          showPlatformFee: ticket?.showPlatformFee || slot.showPlatformFee,
        });
        return {
          baseTotal: acc.baseTotal + line.baseTotal,
          feeAmount: acc.feeAmount + line.feeAmount,
          total: acc.total + line.total,
          showFee: acc.showFee || line.showFee,
        };
      },
      { baseTotal: 0, feeAmount: 0, total: 0, showFee: false }
    );
  }, [event.feePercentage, event.tickets, slots]);

  const applySlots = useCallback(
    (nextSlots: TicketCartSlot[]) => {
      setSlots(nextSlots);
      setValue(
        'attendees',
        nextSlots.map((slot) => emptyAttendee(slot)),
        { shouldValidate: false }
      );
    },
    [setValue]
  );

  const needsPayment = calculateTotals().total > 0;

  const preselectedTicket = useMemo(() => {
    if (!preSelectedTicketId) return null;
    return (
      event.tickets.find((item) => String(item._id) === String(preSelectedTicketId)) || null
    );
  }, [event.tickets, preSelectedTicketId]);

  const linkTicketLimits = useMemo(() => {
    if (!preselectedTicket) return { min: 1, max: 1 };
    return getRegistrationQuantityLimits(preselectedTicket, event.allowGroupRegistration);
  }, [event.allowGroupRegistration, preselectedTicket]);

  useEffect(() => {
    if (preSelectedTicketId) {
      // Link direto (?ticket=): não preenche sozinho — pessoa escolhe a quantidade primeiro.
      clearTicketCart(eventSlug);
      applySlots([]);
      return;
    }
    const nextSlots = expandCartToSlots(loadTicketCart(eventSlug));
    applySlots(nextSlots);
  }, [applySlots, eventSlug, preSelectedTicketId]);

  useEffect(() => {
    setLinkTicketQty(linkTicketLimits.min);
  }, [linkTicketLimits.min, preSelectedTicketId]);

  const confirmLinkTicketQuantity = () => {
    if (!preselectedTicket) {
      setError('Ingresso do link não encontrado ou indisponível.');
      return;
    }
    const { min, max } = linkTicketLimits;
    if (max <= 0) {
      setError('Este ingresso está esgotado.');
      return;
    }
    const qty = Math.min(max, Math.max(min, linkTicketQty));
    clearTicketCart(eventSlug);
    const result = addTicketToCart(
      eventSlug,
      {
        ticketId: String(preselectedTicket._id),
        name: preselectedTicket.name,
        price: preselectedTicket.price,
        showPlatformFee: preselectedTicket.showPlatformFee,
      },
      qty
    );
    if (result.error) {
      setError(result.error);
      return;
    }
    setError(null);
    const nextSlots = expandCartToSlots(result.items);
    applySlots(nextSlots);
    setExpandedPerson(0);
    cartAddTrackedRef.current = true;
    trackMetaAddToCart({
      value: roundMoney((Number(preselectedTicket.price) || 0) * qty),
      numItems: qty,
      contentIds: [String(preselectedTicket._id)],
      contentName: preselectedTicket.name,
    });
  };

  const handleAddTicket = (ticket: TicketData) => {
    const currentCount = slots.filter((slot) => String(slot.ticketId) === String(ticket._id)).length;
    const { max } = getRegistrationQuantityLimits(ticket, event.allowGroupRegistration);
    if (max <= 0) {
      setError('Este ingresso está esgotado.');
      return;
    }
    if (currentCount >= max) {
      setError(`Você já atingiu o máximo de ${max} para ${ticket.name}.`);
      return;
    }
    const result = addTicketToCart(eventSlug, {
      ticketId: String(ticket._id),
      name: ticket.name,
      price: ticket.price,
      showPlatformFee: ticket.showPlatformFee,
    });
    if (result.error) {
      setError(result.error);
      return;
    }
    setError(null);
    applySlots(expandCartToSlots(result.items));
    if (currentCount === 0) {
      trackMetaAddToCart({
        value: Number(ticket.price) || 0,
        numItems: 1,
        contentIds: [String(ticket._id)],
        contentName: ticket.name,
      });
    }
  };

  const handleRemovePerson = (index: number) => {
    const nextAttendees = (getValues('attendees') || []).filter((_, i) => i !== index);
    const nextSlots: TicketCartSlot[] = nextAttendees.map((item) => {
      const ticket = event.tickets.find((t) => String(t._id) === String(item.ticketId));
      return {
        ticketId: item.ticketId,
        name: item.ticketName || ticket?.name || '',
        price: ticket?.price ?? 0,
        showPlatformFee: ticket?.showPlatformFee,
      };
    });
    clearTicketCart(eventSlug);
    const byTicket = nextSlots.reduce<Record<string, { qty: number; meta: TicketCartSlot }>>(
      (acc, slot) => {
        const key = String(slot.ticketId);
        if (!acc[key]) acc[key] = { qty: 0, meta: slot };
        acc[key].qty += 1;
        return acc;
      },
      {}
    );
    Object.values(byTicket).forEach(({ qty, meta }) => {
      addTicketToCart(
        eventSlug,
        {
          ticketId: meta.ticketId,
          name: meta.name,
          price: meta.price,
          showPlatformFee: meta.showPlatformFee,
        },
        qty
      );
    });
    setSlots(nextSlots);
    setValue('attendees', nextAttendees, { shouldValidate: false });
    setError(null);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const settings = await paymentServiceMercadoPago.getInstallmentSettings();
        if (!cancelled) {
          setPlatformInstallmentSettings({
            enabled: settings.enabled !== false,
            maxInstallments: settings.maxInstallments || 12,
          });
        }
      } catch {
        /* keep defaults */
      }
      try {
        const routes = await paymentServiceMercadoPago.getPaymentProviderRoutes({ eventId: String(event._id) });
        if (!cancelled) setPaymentProviderRoutes(routes);
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setPaymentRoutesReady(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [event._id]);

  const mapCustomFields = (person: AttendeeForm) => {
    const mapped: Record<string, unknown> = {};
    fieldsForTicket(person.ticketId).forEach((field) => {
      const value = person.customFields?.[field._id];
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      mapped[field.fieldName] = value;
    });
    return mapped;
  };

  const validatePeopleStep = async () => {
    setValidationErrors([]);
    setError(null);
    const people = getValues('attendees') || [];
    if (people.length < 1) {
      setError('Adicione pelo menos uma pessoa no carrinho.');
      return false;
    }

    const valid = await trigger(['attendees', 'agreeTerms']);
    const messages: string[] = [];
    const allowSameData = people.every((person) => {
      const ticket = event.tickets.find((item) => String(item._id) === String(person.ticketId));
      return ticket?.allowSameAttendeeData !== false;
    });
    const seenEmails = new Set<string>();
    people.forEach((person, index) => {
      const email = String(person.email || '').trim().toLowerCase();
      const ticket = event.tickets.find((item) => String(item._id) === String(person.ticketId));
      if (email) {
        if (!allowSameData && seenEmails.has(email)) {
          messages.push(`Pessoa ${index + 1}: este e-mail já foi usado neste pedido`);
        }
        seenEmails.add(email);
      }
      if (ticket?.isRestricted && ticket.allowedEmails?.length) {
        const allowed = ticket.allowedEmails.some(
          (email) => email.toLowerCase().trim() === person.email.toLowerCase().trim()
        );
        if (!allowed) {
          messages.push(`Pessoa ${index + 1}: e-mail não está na lista de convidados de ${ticket.name}`);
        }
      }
      fieldsForTicket(person.ticketId).forEach((field) => {
        if (!field.isRequired) return;
        const value = person.customFields?.[field._id];
        if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
          messages.push(`Pessoa ${index + 1}: ${field.fieldName} é obrigatório`);
        }
      });
    });
    if (event.requiresBuyerCpf && calculateTotals().total > 0) {
      const cpf = String(getValues('buyerCpf') || '');
      if (!validateCPF(cpf)) {
        messages.push('CPF de quem paga é obrigatório para a nota fiscal do ingresso');
      }
    }
    if (!valid || messages.length > 0) {
      setValidationErrors(messages);
      if (!valid && !getValues('agreeTerms')) {
        messages.push('Aceite os termos para continuar');
        setValidationErrors([...messages]);
      }
      return false;
    }
    return true;
  };

  const createOrder = async () => {
    const form = getValues();
    if (!legalAccepted.length) {
      throw new Error('Não foi possível carregar os documentos legais. Recarregue a página.');
    }
    const first = form.attendees[0];
    const buyer = form.useFirstAsBuyer
      ? { name: first.name, email: first.email, phone: first.phone, document: form.buyerCpf }
      : {
          name: form.buyerName || first.name,
          email: form.buyerEmail || first.email,
          phone: form.buyerPhone || first.phone,
          document: form.buyerCpf,
        };
    const response = await registrationService.createOrder({
      eventId: event._id,
      buyer,
      refCode,
      legalAccepted,
      meta: readMetaBrowserContext(),
      attendees: form.attendees.map((person) => ({
        ticketId: person.ticketId,
        name: person.name,
        email: person.email,
        phone: person.phone,
        customFields: mapCustomFields(person),
      })),
    });
    if (!response.success || !response.data?.primaryRegistrationId) {
      throw new Error(response.message || 'Erro ao criar o pedido.');
    }
    purchaseEventIdRef.current = response.data.orderId || response.data.primaryRegistrationId;
    setRegistrationId(response.data.primaryRegistrationId);
    setRegistrationIds(response.data.registrationIds || [response.data.primaryRegistrationId]);
    setOrderAmount(Number(response.data.amountPaid || 0));
    return response.data;
  };

  const completeOrder = async (primaryId: string, idsFromOrder?: string[]) => {
    clearTicketCart(eventSlug);

    const form = getValues();
    const ids =
      idsFromOrder?.length
        ? idsFromOrder
        : registrationIds.length
          ? registrationIds
          : [primaryId];

    const statusResults = await Promise.all(
      ids.map(async (id) => {
        try {
          const status = await registrationService.checkStatus(id);
          if (status.success && status.data) {
            return {
              qrCodeBase64: status.data.qrCodeBase64,
              checkInCode: status.data.checkInCode,
            };
          }
        } catch {
          /* QR pode chegar por e-mail */
        }
        return undefined;
      })
    );

    const attendees: RegistrationSuccessAttendee[] = form.attendees.map((person, index) => {
      const ticket = event.tickets.find((item) => String(item._id) === String(person.ticketId));
      return {
        registrationId: ids[index] || primaryId,
        participant: {
          name: person.name,
          email: person.email,
          phone: person.phone,
        },
        ticket: ticket
          ? {
              name: ticket.name,
              price: ticket.price,
            }
          : { name: person.ticketName, price: 0 },
        checkInData: statusResults[index],
      };
    });

    const { total } = calculateTotals();

    if (!purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      trackMetaPurchase(total, {
        numItems: form.attendees.length,
        contentIds: form.attendees.map((person) => String(person.ticketId)),
        eventId: purchaseEventIdRef.current || primaryId,
      });
    }

    setRegistrationId(primaryId);
    setSuccessSummary({
      attendees,
      totalAmount: total,
      needsPayment: total > 0,
    });
    setSuccess(true);
  };

  const handleDownloadTicket = async (id: string) => {
    try {
      await registrationService.downloadTicketPDF(
        id,
        `ingresso_${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      );
    } catch (error) {
      console.error('Erro ao baixar ingresso:', error);
      alert('Erro ao baixar ingresso. Por favor, tente novamente ou entre em contato com o suporte.');
    }
  };

  const handleFreeConfirm = async () => {
    const ok = await validatePeopleStep();
    if (!ok) return;
    try {
      setSubmitting(true);
      const order = await createOrder();
      await completeOrder(order.primaryRegistrationId, order.registrationIds);
    } catch (err) {
      setError(formatUnknownError(err, 'Erro ao confirmar o pedido gratuito.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (activeStep !== 0) {
      setActiveStep((step) => step + 1);
      return;
    }
    const ok = await validatePeopleStep();
    if (!ok) return;
    if (!needsPayment) {
      await handleFreeConfirm();
      return;
    }
    if (!registrationId) {
      try {
        setSubmitting(true);
        const order = await createOrder();
        if (order.isFree) {
          await completeOrder(order.primaryRegistrationId, order.registrationIds);
          return;
        }
      } catch (err) {
        setError(formatUnknownError(err, 'Erro ao salvar o pedido.'));
        return;
      } finally {
        setSubmitting(false);
      }
    }
    if (!paymentInfoTrackedRef.current) {
      paymentInfoTrackedRef.current = true;
      const { total } = calculateTotals();
      trackMetaAddPaymentInfo({
        value: total,
        contentIds: (getValues('attendees') || []).map((person) => String(person.ticketId)),
        numItems: (getValues('attendees') || []).length,
      });
    }
    setActiveStep(1);
  };

  const handlePagarmePixRequest = async (cpfDigits: string) => {
    if (!registrationId) {
      setPaymentError('Pedido não encontrado. Volte e tente novamente.');
      return;
    }
    const form = getValues();
    const first = form.attendees[0];
    const { total } = calculateTotals();
    setPaymentProcessing(true);
    setPaymentError(null);
    try {
      const response = await paymentServiceMercadoPago.processPayment({
        ticketId: first.ticketId,
        eventId: event._id,
        quantity: form.attendees.length,
        customerEmail: first.email,
        customerName: first.name,
        customerPhone: first.phone,
        customerCPF: cpfDigits,
        paymentMethodId: 'pix',
        installments: 1,
        transactionAmount: orderAmount || total,
        external_reference: registrationId,
        description: `Ingressos — ${event.title}`,
      });
      if (!response.success || !response.data) {
        setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        return;
      }
      const paymentData = response.data;
      const transactionData = paymentData.point_of_interaction?.transaction_data;
      const qr = transactionData?.qr_code;
      if (String(paymentData?.status || '').toLowerCase() === 'failed') {
        setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        return;
      }
      if (qr) {
        let b64 = transactionData?.qr_code_base64 as string | undefined;
        if (typeof b64 === 'string' && b64.startsWith('data:image')) b64 = b64.split(',')[1];
        setPixQRCodeBase64(b64 || null);
        setPixCode(qr);
        setPixPaymentPending(true);
        return;
      }
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } catch {
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCardPayment = async (
    cardTokenId: string,
    paymentMethodId: string,
    issuerId: string | null,
    installmentsValue: number,
    payer?: { identification?: { number: string } },
    installmentTotalAmount?: number
  ) => {
    if (!registrationId) {
      setPaymentError('Pedido não encontrado.');
      return;
    }
    const form = getValues();
    const first = form.attendees[0];
    const { total } = calculateTotals();
    const finalAmount = roundMoney(
      typeof installmentTotalAmount === 'number' && installmentTotalAmount > total
        ? installmentTotalAmount
        : orderAmount || total
    );
    setPaymentProcessing(true);
    setPaymentError(null);
    try {
      const response = await paymentServiceMercadoPago.processPayment({
        ticketId: first.ticketId,
        eventId: event._id,
        quantity: form.attendees.length,
        customerEmail: first.email,
        customerName: first.name,
        customerCPF: payer?.identification?.number || '',
        cardToken: cardTokenId,
        paymentMethodId,
        issuerId: issuerId || undefined,
        installments: installmentsValue,
        transactionAmount: finalAmount,
        external_reference: registrationId,
      });
      if (!response.success || !response.data) {
        setPaymentError(response.message || 'Erro ao processar pagamento.');
        return;
      }
      const paymentData = response.data;
      if (String(paymentData.payment_method_id || paymentMethodId) === 'pix') {
        const transactionData = paymentData.point_of_interaction?.transaction_data;
        const qr = transactionData?.qr_code;
        if (qr) {
          let b64 = transactionData?.qr_code_base64 as string | undefined;
          if (typeof b64 === 'string' && b64.startsWith('data:image')) b64 = b64.split(',')[1];
          setPixQRCodeBase64(b64 || null);
          setPixCode(qr);
          setPixPaymentPending(true);
        }
        return;
      }
      const failed = ['rejected', 'cancelled', 'refunded', 'charged_back', 'failed', 'canceled'];
      if (failed.includes(paymentData.status)) {
        setPaymentError('Pagamento não concluído. Verifique os dados do cartão.');
        return;
      }
      setCardPaymentPending(true);
    } catch (err) {
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPaymentProcessing(false);
    }
  };

  useEffect(() => {
    if ((!cardPaymentPending && !pixPaymentPending) || !registrationId) return;
    let active = true;
    let attempt = 0;
    const poll = async () => {
      if (!active || attempt >= 8) return;
      try {
        const result = await registrationService.checkStatus(registrationId);
        if (result.success && result.data.paymentStatus === 'paid') {
          setCardPaymentPending(false);
          setPixPaymentPending(false);
          if (registrationId) {
            await completeOrder(registrationId, registrationIds);
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempt += 1;
      if (active) setTimeout(poll, Math.min(5000 * 2 ** attempt, 30000));
    };
    const timer = setTimeout(poll, 4000);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [cardPaymentPending, eventSlug, pixPaymentPending, registrationId]);

  if (slots.length === 0) {
    if (preSelectedTicketId) {
      const line = preselectedTicket
        ? calculateTicketTotalsWithFee({
            unitPrice: preselectedTicket.price,
            quantity: 1,
            feePercentage: event.feePercentage,
            showPlatformFee: preselectedTicket.showPlatformFee,
          })
        : null;
      const { min, max } = linkTicketLimits;
      const canContinue = !!preselectedTicket && max > 0;
      const whenLabel = formatEventWhen(event.startDate);
      const whereLabel = formatEventWhere(event);
      const unitPriceLabel =
        preselectedTicket && preselectedTicket.price > 0 && line
          ? line.showFee
            ? `R$ ${preselectedTicket.price.toFixed(2).replace('.', ',')} + taxa`
            : `R$ ${preselectedTicket.price.toFixed(2).replace('.', ',')}`
          : 'Gratuito';
      const orderTotal =
        preselectedTicket && line
          ? roundMoney(line.total * Math.max(1, linkTicketQty))
          : 0;
      const orderTotalLabel =
        orderTotal > 0 ? `R$ ${orderTotal.toFixed(2).replace('.', ',')}` : 'Gratuito';

      return (
        <Container maxWidth="sm" sx={{ my: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: { xs: 2, md: 3 },
            }}
          >
            {event.coverImage ? (
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 160, sm: 200 },
                  bgcolor: 'grey.200',
                }}
              >
                <Box
                  component="img"
                  src={event.coverImage}
                  alt={event.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.55) 100%)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: 16,
                    right: 16,
                    bottom: 14,
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: '#fff',
                      lineHeight: 1.2,
                      textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    }}
                  >
                    {event.title}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
              {!event.coverImage && (
                <Typography variant="h5" fontWeight={800} gutterBottom sx={{ lineHeight: 1.25 }}>
                  {event.title}
                </Typography>
              )}

              {(whenLabel || whereLabel) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2.5 }}>
                  {whenLabel ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <EventOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.15 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {whenLabel}
                      </Typography>
                    </Box>
                  ) : null}
                  {whereLabel ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.15 }} />
                      <Typography variant="body2" color="text.secondary">
                        {whereLabel}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              )}

              <Typography variant="h6" fontWeight={700} gutterBottom>
                Quantos ingressos?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Escolha a quantidade e depois preencha os dados de cada pessoa.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {!preselectedTicket || max <= 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Este ingresso não está disponível no momento.
                </Alert>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 2.5,
                    bgcolor: 'background.default',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <ConfirmationNumberOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={700}>{preselectedTicket.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {unitPriceLabel}
                        {max > 1 ? ` · até ${max} por pedido` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                    }}
                  >
                    <IconButton
                      aria-label="Diminuir quantidade"
                      disabled={linkTicketQty <= min}
                      onClick={() => setLinkTicketQty((q) => Math.max(min, q - 1))}
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography variant="h4" fontWeight={800} sx={{ minWidth: 48, textAlign: 'center' }}>
                      {linkTicketQty}
                    </Typography>
                    <IconButton
                      aria-label="Aumentar quantidade"
                      disabled={linkTicketQty >= max}
                      onClick={() => setLinkTicketQty((q) => Math.min(max, q + 1))}
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {linkTicketQty} {linkTicketQty === 1 ? 'pessoa' : 'pessoas'}
                    </Typography>
                    <Typography fontWeight={800} color="primary.main">
                      {orderTotalLabel}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!canContinue}
                onClick={confirmLinkTicketQuantity}
                sx={{ textTransform: 'none', fontWeight: 700, py: 1.4 }}
              >
                Continuar · {orderTotalLabel}
              </Button>
              <Button
                component={Link}
                to={`/${eventSlug}`}
                fullWidth
                sx={{ mt: 1.5, textTransform: 'none' }}
              >
                Ver página do evento
              </Button>
            </Box>
          </Paper>
        </Container>
      );
    }

    const now = new Date();
    const availableTickets = (event.tickets || []).filter((ticket) => {
      const expired = ticket.endSaleDate && new Date(ticket.endSaleDate) < now;
      return !expired && ticket.quantity > 0;
    });

    return (
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Paper sx={{ p: { xs: 2, md: 4 } }}>
          <Typography variant="h5" gutterBottom>
            Escolha os ingressos
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Adicione um ingresso para cada pessoa. Depois você preenche os dados de cada participante.
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {availableTickets.length === 0 ? (
            <Alert severity="warning">Nenhum ingresso disponível no momento.</Alert>
          ) : (
            availableTickets.map((ticket) => {
              const line = calculateTicketTotalsWithFee({
                unitPrice: ticket.price,
                quantity: 1,
                feePercentage: event.feePercentage,
                showPlatformFee: ticket.showPlatformFee,
              });
              return (
                <Box
                  key={ticket._id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography fontWeight={600}>{ticket.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ticket.price > 0 ? `R$ ${line.total.toFixed(2)}` : 'Gratuito'}
                    </Typography>
                  </Box>
                  <Button variant="contained" onClick={() => handleAddTicket(ticket)}>
                    Adicionar
                  </Button>
                </Box>
              );
            })
          )}
          <Button component={Link} to={`/${eventSlug}`} sx={{ mt: 3 }}>
            Voltar ao evento
          </Button>
        </Paper>
      </Container>
    );
  }

  if (cardPaymentPending && !success) {
    return (
      <Container maxWidth="sm" sx={{ my: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Processando seu pagamento</Typography>
          <Typography color="text.secondary">
            Assim que o pagamento for aprovado, cada pessoa receberá o próprio ingresso por e-mail.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (success && successSummary?.attendees?.length) {
    return (
      <RegistrationSuccessView
        event={event}
        attendees={successSummary.attendees}
        totalAmount={successSummary.totalAmount}
        needsPayment={successSummary.needsPayment}
        onDownloadTicket={handleDownloadTicket}
      />
    );
  }

  const totals = calculateTotals();
  const people = attendees || [];
  const formatMoney = (value: number) =>
    value > 0 ? `R$ ${value.toFixed(2).replace('.', ',')}` : 'Gratuito';

  return (
    <Container maxWidth="md" sx={{ my: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 3 }, pb: { xs: 12, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          align="center"
          fontWeight={700}
          gutterBottom
          sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}
        >
          {event.title}
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: { xs: 2.5, md: 3.5 },
            '& .MuiStepLabel-label': { typography: { xs: 'caption', sm: 'body2' } },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(() => undefined)} noValidate>
            {activeStep === 0 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      Quem vai participar?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {requireDistinctEmails
                        ? 'Cada pessoa precisa de um e-mail diferente — o QR vai para o e-mail de cada uma.'
                        : 'Preencha os dados de cada pessoa. O QR Code será enviado ao e-mail informado.'}
                    </Typography>
                  </Box>
                  {orderCapacityLabel && (
                    <Chip
                      icon={<ConfirmationNumberOutlinedIcon />}
                      label={orderCapacityLabel}
                      color="primary"
                      variant="outlined"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                  )}
                </Box>

                {people.map((person, index) => {
                  const filledName = String(person.name || '').trim();
                  return (
                    <Accordion
                      key={`${person.ticketId}-${index}`}
                      expanded={expandedPerson === index}
                      onChange={(_, open) => setExpandedPerson(open ? index : false)}
                      disableGutters
                      sx={{
                        mb: 1.5,
                        border: '1px solid',
                        borderColor: expandedPerson === index ? 'primary.main' : 'divider',
                        borderRadius: '12px !important',
                        '&:before': { display: 'none' },
                        overflow: 'hidden',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          minHeight: 64,
                          '& .MuiAccordionSummary-content': {
                            my: 1,
                            alignItems: 'center',
                            gap: 1,
                            pr: 1,
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} noWrap>
                            {filledName || `Participante ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {person.ticketName}
                            {` · ${formatParticipantTicketPrice(person.ticketId)}`}
                          </Typography>
                        </Box>
                        {people.length > 1 && (
                          <IconButton
                            size="small"
                            aria-label={`Remover participante ${index + 1}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePerson(index);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 2.5 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Controller
                              name={`attendees.${index}.name`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Nome completo"
                                  fullWidth
                                  autoComplete="name"
                                  InputLabelProps={{ shrink: true }}
                                  error={!!errors.attendees?.[index]?.name}
                                  helperText={errors.attendees?.[index]?.name?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`attendees.${index}.email`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="E-mail"
                                  type="email"
                                  fullWidth
                                  autoComplete="email"
                                  InputLabelProps={{ shrink: true }}
                                  error={!!errors.attendees?.[index]?.email}
                                  helperText={errors.attendees?.[index]?.email?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`attendees.${index}.emailConfirmation`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Confirme o e-mail"
                                  type="email"
                                  fullWidth
                                  autoComplete="off"
                                  InputLabelProps={{ shrink: true }}
                                  onPaste={(e) => e.preventDefault()}
                                  error={!!errors.attendees?.[index]?.emailConfirmation}
                                  helperText={
                                    errors.attendees?.[index]?.emailConfirmation?.message ||
                                    'Digite de novo para evitar erro de digitação'
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`attendees.${index}.phone`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telefone (com DDD)"
                                  fullWidth
                                  autoComplete="tel"
                                  InputLabelProps={{ shrink: true }}
                                  error={!!errors.attendees?.[index]?.phone}
                                  helperText={errors.attendees?.[index]?.phone?.message}
                                  onChange={(e) => field.onChange(applyPhoneMask(e.target.value))}
                                  inputProps={{ maxLength: 15, inputMode: 'tel' }}
                                />
                              )}
                            />
                          </Grid>
                          <RegistrationCustomFieldInputs
                            fields={fieldsForTicket(person.ticketId)}
                            control={control}
                            namePrefix={`attendees.${index}.customFields`}
                            errors={errors.attendees?.[index]?.customFields as any}
                          />
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {people.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      mb: 2,
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography fontWeight={700} gutterBottom>
                      Resumo
                    </Typography>
                    {ticketBreakdown.map((item) => (
                      <Box
                        key={`${item.name}-${item.qty}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          alignItems: 'baseline',
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {item.qty}× {item.name}
                          {item.qty > 1 ? ` (${item.unitLabel} cada)` : ''}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatMoney(item.lineTotal)}
                        </Typography>
                      </Box>
                    ))}
                    {totals.showFee && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Taxa de serviço
                        </Typography>
                        <Typography variant="body2">
                          R$ {totals.feeAmount.toFixed(2).replace('.', ',')}
                        </Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1.25 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Typography fontWeight={700}>
                        {people.length === 1 ? 'Total' : `Total (${people.length} pessoas)`}
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ m: 0 }}>
                        {formatMoney(totals.total)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <FormControlLabel
                        sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                        control={
                          <Controller
                            name="useFirstAsBuyer"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                sx={{ pt: 0.25 }}
                              />
                            )}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              Quem paga é o Participante 1
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Desmarque só se outra pessoa for pagar o pedido.
                            </Typography>
                          </Box>
                        }
                      />
                      {event.requiresBuyerCpf && totals.total > 0 && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12}>
                            <Controller
                              name="buyerCpf"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="CPF de quem paga *"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  placeholder="000.000.000-00"
                                  helperText="Obrigatório para a nota fiscal do ingresso"
                                  onChange={(e) => field.onChange(applyCpfMask(e.target.value))}
                                  inputProps={{ inputMode: 'numeric', maxLength: 14 }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      )}
                      {!useFirstAsBuyer && (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12}>
                            <Controller
                              name="buyerName"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Nome de quem paga"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name="buyerEmail"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="E-mail de quem paga"
                                  type="email"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name="buyerPhone"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telefone de quem paga"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  onChange={(e) => field.onChange(applyPhoneMask(e.target.value))}
                                  inputProps={{ inputMode: 'tel' }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      <FormControl error={!!errors.agreeTerms} sx={{ width: '100%' }}>
                        <FormControlLabel
                          sx={{ alignItems: 'flex-start', m: 0 }}
                          control={
                            <Controller
                              name="agreeTerms"
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                  sx={{ pt: 0.25 }}
                                />
                              )}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ pt: 0.5 }}>
                              Li e concordo com os{' '}
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('terms');
                                  setLegalModalOpen(true);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                }}
                              >
                                Termos de Uso
                              </Box>
                              ,{' '}
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('privacy');
                                  setLegalModalOpen(true);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                }}
                              >
                                Política de Privacidade
                              </Box>{' '}
                              e{' '}
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('refund');
                                  setLegalModalOpen(true);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                }}
                              >
                                Política de Reembolso
                              </Box>
                              .
                            </Typography>
                          }
                        />
                        {errors.agreeTerms && (
                          <FormHelperText>{errors.agreeTerms.message}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Resumo do pedido
                  </Typography>
                  {slots.map((slot, index) => (
                    <Typography key={`${slot.ticketId}-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                      {index + 1}. {slot.name}
                      {people[index]?.name ? ` — ${people[index].name}` : ''}
                    </Typography>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  {totals.showFee && (
                    <Typography variant="body2" color="text.secondary">
                      Taxa: R$ {totals.feeAmount.toFixed(2)}
                    </Typography>
                  )}
                  <Typography variant="h6" fontWeight={700}>
                    Total: {totals.total > 0 ? `R$ ${totals.total.toFixed(2)}` : 'Gratuito'}
                  </Typography>
                </Paper>

                {needsPayment ? (
                  <>
                    {(pixPaymentPending || cardPaymentPending) && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Aguardando a confirmação do pagamento. Cada pessoa receberá o ingresso por e-mail.
                      </Alert>
                    )}
                    {pixPaymentPending && pixCode ? (
                      <Box sx={{ textAlign: 'center' }}>
                        {pixQRCodeBase64 && (
                          <img
                            src={`data:image/png;base64,${pixQRCodeBase64}`}
                            alt="QR Code Pix"
                            style={{ width: 'min(256px, 100%)', height: 'auto', aspectRatio: '1 / 1' }}
                          />
                        )}
                        <PixCopyCodeField code={pixCode} />
                      </Box>
                    ) : !paymentRoutesReady ? (
                      <CircularProgress />
                    ) : (
                      <CheckoutPaymentRouter
                        amount={orderAmount || totals.total}
                        payerEmail={people[0]?.email}
                        routes={paymentProviderRoutes}
                        checkoutTab={checkoutPaymentTab}
                        onCheckoutTabChange={setCheckoutPaymentTab}
                        onCardTokenReceived={handleCardPayment}
                        onError={() => setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE)}
                        allowInstallments={platformInstallmentSettings.enabled}
                        maxInstallments={platformInstallmentSettings.maxInstallments}
                        customerName={people[0]?.name}
                        onRequestPagarmePix={handlePagarmePixRequest}
                        pagarmePixBusy={paymentProcessing}
                      />
                    )}
                    {paymentError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {paymentError}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="success">Este pedido é gratuito. Confirme para gerar os ingressos.</Alert>
                )}
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {validationErrors.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
                mt: { xs: 3, md: 4 },
                position: { xs: 'fixed', sm: 'static' },
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                p: { xs: 1.5, sm: 0 },
                bgcolor: { xs: 'background.paper', sm: 'transparent' },
                borderTop: { xs: '1px solid', sm: 'none' },
                borderColor: 'divider',
                boxShadow: { xs: '0 -6px 20px rgba(0,0,0,0.06)', sm: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Button
                  fullWidth={isMobile}
                  disabled={activeStep === 0 || submitting || paymentProcessing}
                  onClick={() => setActiveStep(0)}
                  sx={{ minHeight: 48, textTransform: 'none' }}
                >
                  Voltar
                </Button>
                {activeStep === 0 ? (
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    onClick={handleNext}
                    disabled={submitting}
                    sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    {submitting ? (
                      <CircularProgress size={22} />
                    ) : (
                      `Continuar · ${formatMoney(totals.total)}`
                    )}
                  </Button>
                ) : needsPayment ? (
                  !isMobile ? (
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      Finalize o pagamento acima.
                    </Typography>
                  ) : null
                ) : (
                  <Button
                    fullWidth={isMobile}
                    variant="contained"
                    onClick={handleFreeConfirm}
                    disabled={submitting}
                    sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}
                  >
                    {submitting ? <CircularProgress size={22} /> : 'Confirmar pedido'}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Paper>
      <LegalModal open={legalModalOpen} onClose={() => setLegalModalOpen(false)} type={legalModalType} />
    </Container>
  );
};

export default EventCartCheckout;
